import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../supabaseClient';
import cone from '../assets/da2026/cone.png';
import carlbreakDark from '../assets/da2026/carlbreak_dark.png';
import carlbreakLight from '../assets/da2026/carlbreak_light.png';

// Cles historiques : ne pas renommer, sinon les votes deja enregistres sur les
// appareils des joueurs seraient oublies et la page redeviendrait votable.
const VOTER_KEY = 'abrf_prono_voter';
const VOTES_KEY = 'abrf_prono_votes';
const MAX_LEN = 40;

// Mots bloques a la saisie libre. Filet de securite basique : le champ est public,
// l'insertion se fait cote client, donc c'est dissuasif et non infaillible.
// Les organisateurs peuvent supprimer une reponse depuis le dashboard Supabase.
const BLOCKLIST = [
  'connard', 'connasse', 'enculer', 'encule', 'salope', 'pute', 'putain', 'batard',
  'nique', 'niquer', 'fdp', 'ntm', 'pd', 'tapette', 'bite', 'couille', 'chatte',
  'merde', 'fuck', 'shit', 'bitch', 'cunt', 'nigger', 'negre', 'bougnoule', 'youpin',
];

// Identifiant de votant anonyme, persistant par navigateur (appli sans compte).
const getVoterId = () => {
  try {
    let id = localStorage.getItem(VOTER_KEY);
    if (!id) {
      id = uuidv4();
      localStorage.setItem(VOTER_KEY, id);
    }
    return id;
  } catch (e) {
    return uuidv4();
  }
};

const readVotes = () => {
  try {
    return JSON.parse(localStorage.getItem(VOTES_KEY) || '{}');
  } catch (e) {
    return {};
  }
};

const writeVote = (questionId, norm) => {
  try {
    const all = readVotes();
    all[questionId] = norm;
    localStorage.setItem(VOTES_KEY, JSON.stringify(all));
  } catch (e) {}
};

// Normalisation utilisee pour regrouper les reponses libres :
// minuscules, sans accents, ponctuation et espaces multiples ecrases.
export const normalize = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const isBlocked = (s) => {
  const n = normalize(s);
  return BLOCKLIST.some((w) => n === w || n.includes(w));
};

/* ------------------------------------------------------------------ */
/* Resultats                                                           */
/* ------------------------------------------------------------------ */

const Results = ({ rows, myAnswer }) => {
  const [showAll, setShowAll] = useState(false);
  const total = rows.reduce((acc, r) => acc + r.votes, 0);
  const visible = showAll ? rows : rows.slice(0, 3);
  const medals = ['bg-brand-orange', 'bg-brand-ink', 'bg-[#C89B5B]'];

  if (total === 0) {
    return <p className="text-sm text-brand-muted">Tu es le premier à voter sur cette question !</p>;
  }

  return (
    <div>
      <div className="flex flex-col gap-2.5">
        {visible.map((r, i) => {
          const pct = Math.round((r.votes / total) * 100);
          const mine = r.reponse_norm === myAnswer;
          return (
            <div key={r.reponse_norm}>
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`w-5 h-5 shrink-0 rounded-md text-white text-[10px] font-bold flex items-center justify-center ${
                    i < 3 ? medals[i] : 'bg-brand-muted'
                  }`}
                >
                  {i + 1}
                </span>
                <span className={`text-sm truncate ${mine ? 'font-bold text-brand-ink' : 'text-brand-ink'}`}>
                  {r.reponse}
                </span>
                {mine && (
                  <span className="chip chip-soon shrink-0 !px-2 !py-0.5 !text-[10px]">
                    <i className="ri-check-line"></i> Ton vote
                  </span>
                )}
                <span className="ml-auto shrink-0 text-sm font-bold text-brand-orange-deep tabular-nums">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-brand-orange-soft overflow-hidden">
                <div
                  className={`h-full rounded-full ${mine ? 'bg-brand-orange' : 'bg-brand-orange/45'}`}
                  style={{ width: `${Math.max(pct, 3)}%` }}
                ></div>
              </div>
              <div className="text-[11px] text-brand-muted mt-0.5">
                {r.votes} {r.votes > 1 ? 'voix' : 'voix'}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3.5 pt-3.5 border-t border-brand-line">
        <span className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">
          {total} {total > 1 ? 'votes' : 'vote'}
        </span>
        {rows.length > 3 && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-sm font-bold text-brand-orange flex items-center gap-0.5"
          >
            {showAll ? 'Réduire' : `Voir tout (${rows.length})`}
            <i className={showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
          </button>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Une question                                                        */
/* ------------------------------------------------------------------ */

const QuestionCard = ({ question, teams, results, myAnswer, onVote }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // { reponse, equipe_id }
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const voted = !!myAnswer;
  const rows = results || [];

  // Suggestions : equipes du tournoi, ou reponses deja proposees par d'autres.
  const pool =
    question.type === 'equipe'
      ? teams.map((t) => ({ key: String(t.id), label: t.nom, equipe_id: t.id }))
      : rows.map((r) => ({ key: r.reponse_norm, label: r.reponse, equipe_id: null }));

  const q = normalize(query);
  const matches = q ? pool.filter((o) => normalize(o.label).includes(q)) : pool;
  const shown = matches.slice(0, question.type === 'equipe' ? 8 : 5);
  const exact = matches.some((o) => normalize(o.label) === q);
  const canAdd = question.type === 'libre' && q.length > 0 && !exact;

  const submit = async (reponse, equipeId) => {
    const clean = String(reponse).trim().replace(/\s+/g, ' ').slice(0, MAX_LEN);
    if (!clean) return;
    if (question.type === 'libre' && isBlocked(clean)) {
      setError('Cette réponse n’est pas acceptée. Reste fair-play !');
      return;
    }
    setSending(true);
    setError(null);
    const { error: err } = await supabase.from('prono_vote').insert({
      question_id: question.id,
      voter_id: getVoterId(),
      reponse: clean,
      reponse_norm: normalize(clean),
      equipe_id: equipeId || null,
    });
    setSending(false);

    // 23505 = vote deja enregistre pour ce navigateur : on affiche les resultats.
    if (err && err.code !== '23505') {
      setError('Le vote n’a pas pu être enregistré. Réessaie.');
      return;
    }
    onVote(question.id, normalize(clean));
  };

  return (
    <article className="card p-5">
      <div className="flex items-start gap-3 mb-4">
        <span className="w-9 h-9 shrink-0 rounded-xl bg-brand-orange-soft text-brand-orange-deep flex items-center justify-center">
          <i className={question.type === 'equipe' ? 'ri-trophy-fill' : 'ri-star-smile-fill'}></i>
        </span>
        <h3 className="h-display text-lg text-brand-ink leading-tight pt-1">{question.label}</h3>
      </div>

      {voted ? (
        <Results rows={rows} myAnswer={myAnswer} />
      ) : (
        <div>
          <div className="flex items-center gap-2 bg-brand-cream border border-brand-line rounded-full px-4 py-2.5 mb-3">
            <i className="ri-search-line text-brand-orange"></i>
            <input
              type="text"
              value={query}
              maxLength={MAX_LEN}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
                setError(null);
              }}
              placeholder={question.type === 'equipe' ? 'Rechercher une équipe…' : 'Tape un nom…'}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-brand-ink placeholder:text-brand-muted"
            />
          </div>

          {question.type === 'libre' && rows.length > 0 && !query && (
            <div className="text-[11px] font-bold uppercase tracking-wider text-brand-muted mb-2">
              Déjà proposé
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            {shown.map((o) => {
              const on = selected && selected.key === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setSelected(o)}
                  className={`flex items-center gap-2 text-left text-sm rounded-xl px-3.5 py-2.5 border transition-colors ${
                    on
                      ? 'bg-brand-orange text-white border-brand-orange font-semibold'
                      : 'bg-white text-brand-ink border-brand-line'
                  }`}
                >
                  <i className={on ? 'ri-radio-button-line' : 'ri-checkbox-blank-circle-line opacity-30'}></i>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}

            {canAdd && (
              <button
                type="button"
                onClick={() => setSelected({ key: '__new__', label: query.trim(), equipe_id: null })}
                className={`flex items-center gap-2 text-left text-sm rounded-xl px-3.5 py-2.5 border transition-colors ${
                  selected && selected.key === '__new__'
                    ? 'bg-brand-orange text-white border-brand-orange font-semibold'
                    : 'bg-white text-brand-orange-deep border-dashed border-brand-orange/50'
                }`}
              >
                <i className="ri-add-line"></i>
                <span className="truncate">Ajouter «&nbsp;{query.trim()}&nbsp;»</span>
              </button>
            )}

            {shown.length === 0 && !canAdd && (
              <p className="text-sm text-brand-muted py-2">
                {question.type === 'equipe' ? 'Aucune équipe trouvée.' : 'Tape un nom pour proposer une réponse.'}
              </p>
            )}
          </div>

          {error && <p className="text-sm text-brand-orange-deep mt-3">{error}</p>}

          <button
            type="button"
            disabled={!selected || sending}
            onClick={() => submit(selected.label, selected.equipe_id)}
            className="btn-primary w-full mt-4 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white"></div>
                Envoi…
              </>
            ) : (
              <>
                <i className="ri-check-double-line"></i> Voter
              </>
            )}
          </button>
          <p className="text-[11px] text-brand-muted text-center mt-2">
            Un seul vote par question — impossible de revenir en arrière.
          </p>
        </div>
      )}
    </article>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const Elections = () => {
  const [questions, setQuestions] = useState([]);
  const [teamsByTournoi, setTeamsByTournoi] = useState({});
  const [resultsByQuestion, setResultsByQuestion] = useState({});
  const [myVotes, setMyVotes] = useState(readVotes);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchResults = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('prono_resultat')
      .select('question_id,reponse,reponse_norm,votes');
    if (err || !data) return;
    const grouped = {};
    data.forEach((r) => {
      (grouped[r.question_id] = grouped[r.question_id] || []).push(r);
    });
    Object.values(grouped).forEach((rows) => rows.sort((a, b) => b.votes - a.votes));
    setResultsByQuestion(grouped);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: qs, error: qErr } = await supabase
          .from('prono_question')
          .select('id,code,label,type,tournoi_id,ordre')
          .eq('actif', true)
          .order('ordre');
        if (qErr) throw qErr;
        setQuestions(qs || []);

        // Les equipes ne servent qu'aux questions de type 'equipe'. Aujourd'hui il
        // n'y en a plus, mais les questions sont pilotees en base : on charge donc
        // a la demande plutot que de retirer le support.
        if ((qs || []).some((q) => q.type === 'equipe')) {
          const { data: eqs } = await supabase.from('equipe').select('id,nom,tournoi_id');
          const byTournoi = {};
          (eqs || [])
            // La base contient quelques lignes parasites (ex. une equipe nommee "*").
            .filter((e) => e.nom && /[a-zA-Z0-9]/.test(e.nom))
            .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
            .forEach((e) => {
              (byTournoi[e.tournoi_id] = byTournoi[e.tournoi_id] || []).push(e);
            });
          setTeamsByTournoi(byTournoi);
        }

        await fetchResults();
      } catch (e) {
        console.error('Erreur lors du chargement des élections:', e);
        setError(e.message || 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchResults]);

  const handleVote = async (questionId, norm) => {
    writeVote(questionId, norm);
    setMyVotes((v) => ({ ...v, [questionId]: norm }));
    await fetchResults();
  };

  const answered = questions.filter((q) => myVotes[q.id]).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto pb-20">
      {/* Brand head */}
      <header className="relative bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white rounded-b-3xl overflow-hidden shadow-hero px-5 pt-8 pb-6 mb-5">
        <div className="absolute top-0 inset-x-0 h-2 hazard-white"></div>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/85">Le vote du festival</div>
        {/* "LES ELECTIONS" remplit presque toute la largeur : taille fluide pour
            ne pas etre rogne par le overflow-hidden du bandeau sous 360px. */}
        <h1 className="h-display text-[clamp(1.6rem,9.2vw,2.25rem)] mt-2 leading-[0.95]">Les élections<br />du beach</h1>
        <div className="inline-flex items-center gap-2.5 bg-white rounded-2xl px-4 py-2.5 mt-3 shadow-soft">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-muted">by</span>
          <img src={carlbreakDark} alt="Carlbreak" className="h-7 w-auto" />
        </div>
        <p className="text-sm text-white/90 mt-3 max-w-[15rem]">
          Vote dans chaque catégorie, découvre le verdict de la plage.
        </p>
        <img src={cone} alt="" aria-hidden="true" className="absolute right-4 bottom-4 w-14 opacity-95 drop-shadow-lg" />
        {questions.length > 0 && (
          <div className="relative z-10 mt-4 bg-white/15 border border-white/25 rounded-2xl px-4 py-2.5 inline-flex items-center gap-2 text-sm font-semibold">
            <i className="ri-checkbox-circle-fill"></i>
            {answered} / {questions.length} vote{questions.length > 1 ? 's' : ''} enregistré
            {questions.length > 1 ? 's' : ''}
          </div>
        )}
      </header>

      {error ? (
        <div className="px-5">
          <div className="card p-6 text-center">
            <h2 className="h-display text-xl text-brand-ink mb-2">Oups…</h2>
            <p className="text-sm text-brand-muted mb-1">Erreur lors du chargement des élections : {error}</p>
            <button onClick={() => window.location.reload()} className="btn-primary mt-3">
              <i className="ri-refresh-line"></i> Réessayer
            </button>
          </div>
        </div>
      ) : questions.length === 0 ? (
        <div className="px-5">
          <div className="card p-8 text-center flex flex-col items-center">
            <img src={cone} alt="" className="h-20 w-auto mb-4" />
            <h2 className="h-display text-xl mb-2 text-brand-ink">Bientôt</h2>
            <p className="text-sm text-brand-muted">Les urnes ouvriront juste avant le coup d'envoi.</p>
          </div>
        </div>
      ) : (
        <div className="px-5 flex flex-col gap-4">
          {questions.map((q) => (
            <QuestionCard
              key={q.id}
              question={q}
              teams={teamsByTournoi[q.tournoi_id] || []}
              results={resultsByQuestion[q.id]}
              myAnswer={myVotes[q.id]}
              onVote={handleVote}
            />
          ))}
        </div>
      )}

      {/* Sponsor de la section */}
      <section className="px-5 pt-6">
        <div className="relative rounded-3xl overflow-hidden px-6 py-6 text-center text-white bg-brand-ink">
          <div className="absolute top-0 inset-x-0 h-1.5 hazard"></div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/55 mt-1 mb-4">
            Les élections du beach, offertes par
          </div>
          <img src={carlbreakLight} alt="Carlbreak" className="h-11 w-auto mx-auto" />
          <p className="text-[11px] text-white/55 mt-4 leading-relaxed">
            L'abus d'alcool est dangereux pour la santé. À consommer avec modération.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Elections;
